import { CbamSimulationGood, useDeleteCbamSimulationGood } from '@/features/cbam-simulations-v2/hooks/cbamSimulationGoods'
import useI18n from '@/shared/hooks/usei18n'
import { useSession } from '@/shared/hooks/useSession'
import { Button, GenericDialog } from '@/common'
import { useCallback } from 'react'
import toast from 'react-hot-toast'
import { Trans } from 'react-i18next'
import { trackCbamSimulationSignal } from '@/features/cbam-simulations-v2/analytics/cbamSimulationClarity'

export default function CbamSimulationGoodDeleteDialog({
  isOpen,
  close,
  cbamSimulationId,
  simulationGood
}: {
  isOpen: boolean
  close: () => void
  cbamSimulationId: string
  simulationGood: CbamSimulationGood
}) {
  const { t } = useI18n()
  const organizationId = useSession().session.organization.id
  const { mutateAsync: deleteSimulationGood, isPending: isDeleting } = useDeleteCbamSimulationGood(
    organizationId,
    cbamSimulationId,
    simulationGood.id
  )

  const goodName = simulationGood.description || simulationGood.cnCode

  const onDeleteClick = useCallback(async () => {
    try {
      await deleteSimulationGood()
      trackCbamSimulationSignal('simulations:good:delete:succeeded')
      toast.success(
        t(
          'cbamSimulations:cbamSimulationDetails.dialogs.deleteSimulationGood.notifications.success'
        )
      )
      close()
    } catch (error) {
      console.error(error)
      trackCbamSimulationSignal('simulations:good:delete:failed')
      toast.error(
        t('cbamSimulations:cbamSimulationDetails.dialogs.deleteSimulationGood.notifications.error')
      )
    }
  }, [deleteSimulationGood, close, t])

  return (
    <GenericDialog
      isOpen={isOpen}
      close={close}
      title={t('cbamSimulations:cbamSimulationDetails.dialogs.deleteSimulationGood.title')}
      size="md"
    >
      <div className="space-y-4">
        <p>
          <Trans
            i18nKey="cbamSimulations:cbamSimulationDetails.dialogs.deleteSimulationGood.description"
            values={{ name: goodName }}
            components={{ strong: <strong /> }}
          />
        </p>
        <footer className="gap-2 flex justify-end">
          <Button variant="neutral" onClick={close}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="danger" onClick={onDeleteClick} isLoading={isDeleting}>
            {t('common:actions.delete')}
          </Button>
        </footer>
      </div>
    </GenericDialog>
  )
}
